import { useMemo, useState } from 'react';
import { Button } from '@library/components/buttons';
import type { DateRange } from '@library/types/dates';
import { DepositsTable } from '@bo-components/deposits/DepositsTable';
import type { FilterKey } from '@library/types/filters';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { PageTitle } from '@library/components/PageTitle';
import { PaymentMethodsTable } from '@bo-components/payments/methods/PaymentMethodsTable';
import { PaymentsDateRange } from '@library/components/PaymentsDateRange';
import { PaymentsDepositsFilters } from '@library/components/PaymentsDepositsFilters';
import type { PaymentsRequest } from '@library/types/payments';
import { PaymentsSearch } from '@library/components/inputs/PaymentsSearch';
import { PaymentsWithdrawalsFilters } from '@library/components/PaymentsWithdrawalsFilters';
import type { SelectOption } from '@library/types/utils';
import { TabFilter } from '@library/components/filters';
import { WithdrawalsTable } from '@bo-components/withdrawals/WithdrawalsTable';
import resetIconUrl from '@library/assets/images/icons/gray-refresh-icon.svg';
import { useGetPaymentsTransactionsQuery } from '@bo-services/paymentsApi';
import { usePaymentsFilters } from '@bo-hooks/usePaymentsFilters';
import { useSentrySetDashBoardTag } from '@bo-hooks/sentry/useSentrySetDashBoardTag';

export const PaymentsDashboard = () => {
  useSentrySetDashBoardTag({ dashboardName: 'Payments' });

  const {
    fromDate,
    handleSearch,
    handleSelectDateRange,
    resetFilters,
    searchValue,
    selectedEndDate,
    selectedStartDate,
    toDate,
    changeDepositsFiltersValue,
    depositsFilters,
    changeWithdrawalsFiltersValue,
    withdrawalsFilters
  } = usePaymentsFilters();

  const options: Array<SelectOption> = [
    { key: 'deposits', name: 'Deposits', value: 'deposits' },
    { key: 'withdrawals', name: 'Withdrawals', value: 'withdrawals' },
    { key: 'methods', name: 'Methods', value: 'methods' }
  ];
  const filterName: FilterKey = 'paymentsMode';

  let email: string | undefined;
  let id: string | undefined;
  if (searchValue.includes('@')) {
    email = searchValue;
  } else if (searchValue) {
    id = searchValue;
  }

  const paymentsQueryParams = useMemo<PaymentsRequest>(() => {
    return {
      email,
      id
    };
  }, [email, id]);

  const [mode, setMode] = useState('deposits');
  const [currentPage] = useState(1);

  const { data, isLoading, isFetching, isError, refetch } =
    useGetPaymentsTransactionsQuery({
      ...paymentsQueryParams,
      page: currentPage,
      perPage: 100000
    });

  const handleDepositStatusChange = (value: string) => {
    changeDepositsFiltersValue('depositStatus', value);
  };

  const handleDepositMethodTypeChange = (value: string) => {
    changeDepositsFiltersValue('depositMethodType', value);
  };

  const handleWithdrawalStatusChange = (value: string) => {
    changeWithdrawalsFiltersValue('withdrawalStatus', value);
  };

  const handleWithdrawalMethodChange = (value: string) => {
    changeWithdrawalsFiltersValue('withdrawalMethod', value);
  };

  const handleResetFilters = () => {
    resetFilters();
  };

  const handleSearchChange = () => {
    if (data) {
      handleSearch('');
      resetFilters();
    }
  };

  const handleSearchAction = (value: string) => {
    refetch().catch(() => {});
    handleSearch(value);
  };

  const handleChangeDate = (value: DateRange) => {
    handleSelectDateRange(value);
  };

  const handleModeSelect = (newMode: string) => {
    setMode(newMode);
  };

  const hasUser = data?.user;
  const hasDeposits = data?.deposits && data?.deposits?.length > 0;
  const hasWithdrawals = data?.withdrawals && data?.withdrawals?.length > 0;
  const notAnotherRenderer = !isError && !isFetching && !isLoading;
  const inMethodsMode = mode === 'methods';
  const showError = isError && !inMethodsMode;

  return (
    <div className="h-full w-auto">
      <div className="no-scrollbar overflow-scroll px-6 pb-2 pt-3">
        <PageTitle title="Payments" />
        <div className="mt-4 grid grid-cols-[repeat(10,1fr)] gap-4 pb-5">
          <PaymentsSearch
            handleSearchChange={handleSearchChange}
            handleSearch={handleSearchAction}
          />
          {notAnotherRenderer && hasUser && (
            <div className="border-soFresh-140 bg-soFresh-110 col-span-12 max-w-screen-md rounded-md border px-3 py-2 text-xs">
              <div className="h-5 text-white">{`${data.user.first_name} ${data.user.last_name}`}</div>
              <div className="text-soClean-140">{`${data.user.id} \u2022 ${data.user.email}`}</div>
            </div>
          )}
          <div className="inline-flex" data-testid="PaymentsMode">
            <TabFilter
              filterClassName="w-max"
              filterName={filterName}
              handleSelect={handleModeSelect}
              options={options}
              selectedValue={mode}
              testId="payments-tab-filter"
            />
          </div>
          {!inMethodsMode && (
            <>
              <PaymentsDateRange
                allowPastDates
                handleSelectDateRange={handleChangeDate}
                selectedEndDate={selectedEndDate}
                selectedStartDate={selectedStartDate}
              />
              {mode === 'deposits' && (
                <PaymentsDepositsFilters
                  filters={depositsFilters}
                  onDepositStatusChange={handleDepositStatusChange}
                  onDepositMethodTypeChange={handleDepositMethodTypeChange}
                />
              )}
              {mode === 'withdrawals' && (
                <PaymentsWithdrawalsFilters
                  filters={withdrawalsFilters}
                  onWithdrawalStatusChange={handleWithdrawalStatusChange}
                  onWithdrawalMethodChange={handleWithdrawalMethodChange}
                />
              )}
              <Button
                aria-label="reset filters"
                className="text-soClean-140 w-[120px] text-xs"
                data-testid="reset-filters"
                title="NextButton"
                onClick={handleResetFilters}
                type="button"
              >
                <img alt="reset" className="mr-2 inline" src={resetIconUrl} />
                <span>Reset Filters</span>
              </Button>
            </>
          )}
        </div>
        {isLoading && <Loading loadingMessage="Loading Payments" />}
        {showError && <NoResults />}
        {notAnotherRenderer && hasDeposits && mode === 'deposits' && (
          <div className="mt-4 pb-5">
            <DepositsTable
              data={data}
              filters={depositsFilters}
              fromDate={fromDate}
              toDate={toDate}
              dataRefetch={refetch}
            />
          </div>
        )}
        {notAnotherRenderer && hasWithdrawals && mode === 'withdrawals' && (
          <div className="mt-4 pb-5">
            <WithdrawalsTable
              data={data}
              filters={withdrawalsFilters}
              fromDate={fromDate}
              toDate={toDate}
            />
          </div>
        )}
        {mode === 'methods' && data?.user && (
          <div className="mt-4 pb-5">
            <PaymentMethodsTable user={data?.user} userQuery={searchValue} />
          </div>
        )}
      </div>
    </div>
  );
};
